"use client";
import { cn } from "#/lib/utils";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import { ChangeEventHandler, ReactElement, useState } from "react";

export const Input = ({
  secure,
  placeholder,
  register = {},
  title,
  inputStyles,
  rootStyles,
  searchable,
  id,
  editable,
  disabled,
  rightHeader,
  titleStyles,
  onBlur,
  onChange,
  onSubmit,
  icon,
}: {
  placeholder?: string;
  rightHeader?: ReactElement;
  rootStyles?: any;
  register?: any;
  searchable?: boolean;
  secure?: boolean;
  title?: string;
  inputStyles?: any;
  titleStyles?: any;
  disabled?: boolean;
  editable?: boolean;
  onBlur?: () => void;
  id?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onSubmit?: () => void;
  icon?: ReactElement;
}) => {
  const registerProps = register || {};

  // Handle the case where onChange might be undefined
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e);
    } else if (registerProps.onChange) {
      registerProps.onChange(e);
    }
  };

  const isDisabled = disabled === true || editable === false;

  return (
    <div
      className={clsx(cn(`relative`, rootStyles), {
        "cursor-not-allowed": isDisabled,
      })}
    >
      {title && (
        <label
          className={cn(
            "bg-card absolute line-clamp-1 left-[0.625rem] px-1 text-muted text-[11px] font-circular-std font-500 top-[-7px] z-[5]",
            titleStyles,
          )}
        >
          {title}
        </label>
      )}
      {rightHeader && (
        <label
          className={cn(
            "bg-card absolute right-[0.625rem] px-1 text-muted text-[11px] font-circular-std font-500 top-[-7px] z-[5]",
            titleStyles,
          )}
        >
          {rightHeader}
        </label>
      )}
      <div className="relative z-[1]">
        {searchable ? (
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 text-muted" />
        ) : null}
        {icon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10 pointer-events-none">
            {icon}
          </div>
        )}
        <input
          title={title}
          placeholder={placeholder}
          onBlur={onBlur}
          data-testid={id}
          aria-label={title}
          readOnly={editable === false}
          disabled={isDisabled}
          spellCheck={false}
          onKeyDown={(e) => {
            if (e.key === "Enter" && onSubmit) {
              onSubmit();
            }
          }}
          className={cn(
            `text-foreground placeholder-muted outline-none h-10 w-full px-3 bg-transparent text-sm rounded-lg border border-border focus:ring-0 focus:border-primary`,
            {
              "cursor-not-allowed bg-background": isDisabled,
              "bg-background pl-9 border-none": searchable,
              "pl-10": icon && !searchable,
            },
            inputStyles,
          )}
          type={secure ? "password" : "text"}
          {...registerProps}
          onChange={handleChange}
        />
      </div>
    </div>
  );
};
